/**
 * One presentation of the Step 6 capture record, for every surface that shows
 * it (HG-3B G3-04).
 *
 * Step 8, the pilot PDF and the pilot DOCX must not each decide what a
 * dimension, a limiting factor, an override or a development level looks like
 * on a page. Three independent decisions would drift, and a report whose
 * dimensions read differently from the screen that recorded them is worse than
 * no report. So the decision is taken once, here, and the three surfaces render
 * what this module returns.
 *
 * Requirement sources:
 *   - GCH-14 / issue EX-02: exports carry the dimensions, the limiting factor,
 *     the ICAO references and the framework version. A grade without them is an
 *     assertion, not evidence.
 *   - GCH-35 / ICD 9.3: a value that was never recorded is presented as "not
 *     recorded". Never zero, never a default, never inferred. That rule applies
 *     to a historical row, whose dimensions do not exist, exactly as it applies
 *     to a live row whose instructor has not reached Step 6 yet. This module
 *     has no other way of writing an absent value.
 *   - GCH-13 / ICD 8.7: where an override occurred, what is shown is the
 *     proposal, the final grade, the category, the actor and the timestamp. The
 *     note appears if and only if the category is `other` (DEC-28), because on
 *     every other category there is no note to show.
 *   - ICD 8.7: the limiting factor is the instructor's recorded judgment. This
 *     module never derives it and never recomputes it; where it is absent it
 *     says so.
 *   - ICD 3.1.4: per-competency development level and its source. The ICD is
 *     silent on how to present them, so they follow the dimensions' styling.
 *
 * Word pictures come from the resolved framework at run time (GCH-06), never
 * from a table in this file: a tenant framework version may word its scales
 * differently, and the report must show the wording that applied.
 */

import type { ResolvedFramework } from '../framework/loader';
import {
  DIMENSION_KEYS,
  NOT_RECORDED_LABEL,
  dimensionOptions,
  type DimensionKey,
} from './dimensions';

/** Dimension names, shared so Step 6, Step 8 and both generators agree. */
export const DIMENSION_LABEL: Record<DimensionKey, string> = {
  how_many: 'How many',
  how_often: 'How often',
  tem_outcome: 'TEM outcome',
};

/** ICD 8.4 OverrideCategoryV1, in the wording Step 6 offers. */
export const OVERRIDE_CATEGORY_LABEL: Record<string, string> = {
  evidence_not_captured_by_dimensions: 'Evidence not captured by the dimensions',
  dimension_recorded_in_error: 'Dimension recorded in error',
  tem_outcome_not_attributable: 'TEM outcome not attributable to the trainee',
  other: 'Other',
};

/** ICD 8.8 DevelopmentLevelV1. */
export const DEVELOPMENT_LEVEL_LABEL: Record<string, string> = {
  beginner: 'Beginner',
  building_competencies: 'Building Competencies',
  proficient: 'Proficient',
};

/** ICD 8.8 DevelopmentLevelSourceV1. The PRS 3.3 trainee rung is Elevate-only. */
export const DEVELOPMENT_LEVEL_SOURCE_LABEL: Record<string, string> = {
  default_mapping: 'Default mapping',
  instructor_override: 'Instructor override',
  operator_setting: 'Operator setting',
};

/** The stored capture record of one competency, as the row carries it. */
export interface CaptureRecord {
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  override_actor?: string | null;
  override_at?: string | null;
  development_level?: string | null;
  development_level_source?: string | null;
  development_level_assigned_at?: string | null;
  historical?: boolean | null;
}

/**
 * The columns an assessments_pilot row carries the capture record in.
 *
 * Wider than CaptureRecord on purpose: a live row is what every surface
 * actually holds, and it carries plenty this module has no interest in.
 */
export interface CaptureRowColumns {
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  override_actor?: string | null;
  override_at?: string | null;
  development_level?: string | null;
  development_level_source?: string | null;
  development_level_assigned_at?: string | null;
  historical?: boolean | null;
}

/**
 * One CFX-11 materialised performance criterion as a surface displays it
 * (MW-01 row i).
 *
 * assessments_pilot.performance_criteria (migration 232:560) is JSONB holding
 * an array of MaterialisedPerformanceCriterionV1, frozen into the record at
 * assessment time so a later framework version cannot restate what was graded.
 * conditions and competency_standard stay unknown because the registry may
 * hold either the text or an object carrying it, and a surface must not guess.
 */
export interface PerformanceCriterionForDisplay {
  behaviour_code?: string;
  conditions?: unknown;
  competency_standard?: unknown;
}

/** The criteria array off a row, or an empty list. Never throws on shape. */
export function asPerformanceCriteria(value: unknown): PerformanceCriterionForDisplay[] {
  return Array.isArray(value) ? (value as PerformanceCriterionForDisplay[]) : [];
}

/** The readable text of a criterion field, or null where there is none. */
export function criterionText(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const text = (value as Record<string, unknown>).text;
    if (typeof text === 'string') return text;
  }
  return null;
}

/**
 * Lift the capture record off a live assessments_pilot row (HG-3C G3-11).
 *
 * Copies, never derives. It does not recompute proposed_grade from the
 * dimensions and does not infer the limiting factor: ICD 8.7 makes both stored
 * judgments that a recomputation must not overwrite.
 *
 * Undefined is normalised to null here, once, so that every surface hands
 * presentCapture the same shape. That matters more than it looks: presentCapture
 * distinguishes override_flag false ("No override") from override_flag null
 * ("not recorded"), and a surface that let undefined through would be relying on
 * `?? null` happening to be applied somewhere downstream.
 */
export function captureRecordFromRow(
  row: CaptureRowColumns | null | undefined,
): CaptureRecord {
  const r = row ?? {};
  return {
    how_many: r.how_many ?? null,
    how_often: r.how_often ?? null,
    tem_outcome: r.tem_outcome ?? null,
    limiting_factor: r.limiting_factor ?? null,
    proposed_grade: r.proposed_grade ?? null,
    override_flag: r.override_flag ?? null,
    override_category: r.override_category ?? null,
    override_note: r.override_note ?? null,
    override_actor: r.override_actor ?? null,
    override_at: r.override_at ?? null,
    development_level: r.development_level ?? null,
    development_level_source: r.development_level_source ?? null,
    development_level_assigned_at: r.development_level_assigned_at ?? null,
    historical: r.historical ?? null,
  };
}

export interface DimensionDisplay {
  key: DimensionKey;
  label: string;
  /** The stored ICD enum code, or null where nothing was recorded. */
  code: string | null;
  /** Registry position 1 to 5, or null when the code does not resolve. */
  position: number | null;
  /** Word picture from the registry, or NOT_RECORDED_LABEL. */
  text: string;
}

export interface OverrideDisplay {
  proposedGrade: number | null;
  recordedGrade: number | null;
  category: string | null;
  categoryLabel: string;
  /** Present if and only if the category is `other` (DEC-28). */
  note: string | null;
  actor: string | null;
  at: string | null;
}

export interface CapturePresentation {
  /** ICD 9.3 historical marker, as stored. */
  historical: boolean;
  /** True when all three dimensions and the limiting factor are present. */
  dimensionsRecorded: boolean;
  dimensions: DimensionDisplay[];
  /** Limiting-factor label, or NOT_RECORDED_LABEL. Never derived (ICD 8.7). */
  limitingFactor: string;
  /** Proposal as a display string, or NOT_RECORDED_LABEL. */
  proposedGrade: string;
  /**
   * The override record, present only where one occurred. Null covers both
   * "graded in agreement with the proposal" and "nothing recorded"; the two are
   * distinguished by overrideSummary.
   */
  override: OverrideDisplay | null;
  /** One line for a table cell: the override, "No override", or not recorded. */
  overrideSummary: string;
  developmentLevel: string;
  developmentLevelSource: string;
}

function wordPicture(
  framework: Pick<ResolvedFramework, 'dimensionScales'> | null | undefined,
  key: DimensionKey,
  code: string | null | undefined,
): { position: number | null; text: string } {
  if (!code) return { position: null, text: NOT_RECORDED_LABEL };
  const option = dimensionOptions(framework, key).find((o) => o.code === code);
  // A code the framework version does not carry is shown as the code itself.
  // It was recorded, so it is not "not recorded"; the registry simply has no
  // wording for it in this version, and inventing one would be worse.
  return option
    ? { position: option.position, text: option.wordPicture }
    : { position: null, text: code };
}

function labelOrNotRecorded(map: Record<string, string>, code: string | null | undefined): string {
  if (!code) return NOT_RECORDED_LABEL;
  return map[code] ?? code;
}

/**
 * Build the presentation of one competency's capture record.
 *
 * The only thing this function decides is how absence is written. Everything
 * else is a lookup. A historical row and a live row that has not reached Step 6
 * produce the same "not recorded" text on purpose: the report states what was
 * recorded, and the reason it was not recorded belongs to the row's historical
 * flag, not to the wording of an empty cell.
 */
export function presentCapture(
  record: CaptureRecord | null | undefined,
  recordedGrade: number | null | undefined,
  framework: Pick<ResolvedFramework, 'dimensionScales'> | null | undefined,
): CapturePresentation {
  const row = record ?? {};

  const dimensions: DimensionDisplay[] = DIMENSION_KEYS.map((key) => {
    const code = row[key] ?? null;
    const { position, text } = wordPicture(framework, key, code);
    return { key, label: DIMENSION_LABEL[key], code, position, text };
  });

  const dimensionsRecorded =
    dimensions.every((d) => d.code != null && d.code !== '') &&
    !!row.limiting_factor;

  const limitingFactor = row.limiting_factor
    ? (DIMENSION_LABEL[row.limiting_factor as DimensionKey] ?? row.limiting_factor)
    : NOT_RECORDED_LABEL;

  const proposed = row.proposed_grade ?? null;
  const recorded = recordedGrade ?? null;

  const override: OverrideDisplay | null = row.override_flag
    ? {
        proposedGrade: proposed,
        recordedGrade: recorded,
        category: row.override_category ?? null,
        categoryLabel: labelOrNotRecorded(OVERRIDE_CATEGORY_LABEL, row.override_category),
        // DEC-28: a note exists only for `other`. Showing one anywhere else
        // would imply a justification requirement that was withdrawn.
        note: row.override_category === 'other' ? (row.override_note ?? null) : null,
        actor: row.override_actor ?? null,
        at: row.override_at ?? null,
      }
    : null;

  const overrideSummary = override
    ? `Proposed ${override.proposedGrade ?? NOT_RECORDED_LABEL.toLowerCase()}, recorded ${
        override.recordedGrade ?? NOT_RECORDED_LABEL.toLowerCase()
      }: ${override.categoryLabel}`
    : row.override_flag === false
      ? 'No override'
      : NOT_RECORDED_LABEL;

  return {
    historical: row.historical === true,
    dimensionsRecorded,
    dimensions,
    limitingFactor,
    proposedGrade: proposed == null ? NOT_RECORDED_LABEL : String(proposed),
    override,
    overrideSummary,
    developmentLevel: labelOrNotRecorded(DEVELOPMENT_LEVEL_LABEL, row.development_level),
    developmentLevelSource: labelOrNotRecorded(
      DEVELOPMENT_LEVEL_SOURCE_LABEL,
      row.development_level_source,
    ),
  };
}

/**
 * The framework version stamp for an export footer or header (GCH-32, GCH-14).
 *
 * Prefers what the assessment rows were stamped with at grading time, because
 * that is the version the grade was actually recorded against. The resolved
 * framework is the fallback for a payload that carries no stamp. Where neither
 * is available the caller is told so explicitly rather than shown a guess.
 */
export function frameworkVersionStamp(
  stamped: { frameworkId?: string | null; frameworkVersion?: string | null } | null | undefined,
  framework?: Pick<ResolvedFramework, 'frameworkId' | 'versionIdentifier' | 'versionLabel'> | null,
): string {
  const id = stamped?.frameworkId ?? framework?.frameworkId ?? null;
  const version = stamped?.frameworkVersion ?? framework?.versionIdentifier ?? null;

  if (!version) return `Framework version: ${NOT_RECORDED_LABEL.toLowerCase()}`;

  // The label is display only and never a key (CFX-01), so it is additive to
  // the opaque identifier rather than a replacement for it.
  const label =
    framework && framework.versionIdentifier === version && framework.versionLabel
      ? ` ${framework.versionLabel}`
      : '';

  return `Framework: ${id ?? 'unknown lineage'} version ${version}${label}`;
}
